(() => {
  const FREE_GIFT_KEY = "_free_gift";
  const FREE_GIFT_PARENT_KEY = "_free_gift_parent";
  const FREE_GIFT_CHOICE_KEY = "_free_gift_choice";
  const STATE_KEY = "farmry_free_gift_cart_state";
  const CART_RELOAD_GUARD_KEY = "farmry_free_gift_cart_reload_guard";
  const MAP_SECTION_ID = "free-gift-map";
  const SYNC_DEBOUNCE_MS = 360;
  const MINI_CART_SECTION = "mini-cart";
  let syncing = false;
  let pendingResync = false;
  let syncDebounceTimer = null;
  let cachedGiftMap = null;
  let cachedGiftMapKey = "";

  const cartUrl = () => window.themeVariables.routes.cartUrl;
  const isCartPage = () => window.themeVariables?.settings?.pageType === "cart";
  const miniCartSections = () => (isCartPage() ? undefined : MINI_CART_SECTION);

  const getProperty = (item, key) => {
    if (!item || !item.properties) return "";
    return item.properties[key] || "";
  };

  const isFreeGift = (item) => String(getProperty(item, FREE_GIFT_KEY)).toLowerCase() === "true";

  const cartNeedsGiftSync = (cart) => {
    return (cart.items || []).some((item) => {
      if (isFreeGift(item)) return true;
      return String(getProperty(item, "_has_free_gift")).toLowerCase() === "true";
    });
  };

  const getCartParentSignature = (cart) => {
    return (cart.items || [])
      .filter((item) => !isFreeGift(item))
      .map((item) => String(item.product_id))
      .sort()
      .join(",");
  };

  const readState = () => {
    try {
      return JSON.parse(sessionStorage.getItem(STATE_KEY) || "{}");
    } catch (error) {
      return {};
    }
  };

  const writeState = (cart) => {
    const parents = {};
    const gifts = {};

    (cart.items || []).forEach((item) => {
      if (isFreeGift(item)) {
        gifts[item.key] = item.quantity;
        return;
      }
      const productId = String(item.product_id);
      parents[productId] = (parents[productId] || 0) + item.quantity;
    });

    try {
      sessionStorage.setItem(STATE_KEY, JSON.stringify({ parents, gifts }));
    } catch (error) {
      // Ignore quota / private mode failures
    }
  };

  const getParentQuantityMap = (cart) => {
    const qtyByParentId = {};

    (cart.items || []).forEach((item) => {
      if (isFreeGift(item)) return;
      const productId = String(item.product_id);
      qtyByParentId[productId] = (qtyByParentId[productId] || 0) + item.quantity;
    });

    return qtyByParentId;
  };

  const getParentLineChoice = (item) => {
    const choice = getProperty(item, FREE_GIFT_CHOICE_KEY);
    if (!choice) return null;
    const variantId = Number(choice);
    return Number.isFinite(variantId) && variantId > 0 ? variantId : null;
  };

  /**
   * Required free-gift qty for a parent product + gift variant.
   * Pick-one: only parent lines whose _free_gift_choice matches the gift variant.
   */
  const getRequiredGiftQty = (cart, parentId, giftVariantId, pickOne) => {
    let total = 0;

    (cart.items || []).forEach((item) => {
      if (isFreeGift(item)) return;
      if (String(item.product_id) !== String(parentId)) return;

      if (pickOne) {
        const choice = getParentLineChoice(item);
        if (!choice || Number(choice) !== Number(giftVariantId)) return;
      }

      total += item.quantity;
    });

    return total;
  };

  const sumThemeFreeGiftQty = (cart, parentId, giftVariantId) => {
    let total = 0;

    (cart.items || []).forEach((item) => {
      if (!isFreeGift(item)) return;
      if (String(getProperty(item, FREE_GIFT_PARENT_KEY)) !== String(parentId)) return;
      if (Number(item.variant_id) !== Number(giftVariantId)) return;
      total += item.quantity;
    });

    return total;
  };

  const isAppManagedFreeGiftLine = (item) => {
    if (isFreeGift(item)) return false;
    if (Number(item.final_line_price) !== 0) return false;
    const discounts = item.line_level_discount_allocations || item.discounts || [];
    return discounts.some((entry) => {
      const title = (entry.discount_application && entry.discount_application.title) || entry.title || "";
      return /free gift/i.test(title);
    });
  };

  const sumAppFreeGiftQty = (cart, giftVariantId) => {
    let total = 0;

    (cart.items || []).forEach((item) => {
      if (Number(item.variant_id) !== Number(giftVariantId)) return;
      if (!isAppManagedFreeGiftLine(item)) return;
      total += item.quantity;
    });

    return total;
  };

  const findPaidSibling = (cart, variantId, updates) => {
    return (cart.items || []).find((item) => {
      if (item.variant_id !== variantId || isFreeGift(item)) return false;
      if (updates[item.key] === 0) return false;
      return true;
    });
  };

  const queuePaidExcess = (cart, updates, paidAdds, variantId, excess) => {
    const paidSibling = findPaidSibling(cart, variantId, updates);
    if (paidSibling) {
      const baseQty = updates[paidSibling.key] != null ? updates[paidSibling.key] : paidSibling.quantity;
      updates[paidSibling.key] = baseQty + excess;
      return;
    }

    const existingAdd = paidAdds.find((entry) => entry.id === variantId);
    if (existingAdd) {
      existingAdd.quantity += excess;
    } else {
      paidAdds.push({ id: variantId, quantity: excess });
    }
  };

  const normalizeGiftMapEntry = (entry) => {
    if (!entry) {
      return { pickOne: false, gifts: [] };
    }
    if (Array.isArray(entry)) {
      return { pickOne: false, gifts: entry };
    }
    return {
      pickOne: entry.pick_one === true,
      gifts: Array.isArray(entry.gifts) ? entry.gifts : []
    };
  };

  const getAllowedGiftVariantIds = (gifts) => {
    const ids = new Set();
    (gifts || []).forEach((gift) => {
      const id = Number(gift && gift.id);
      if (Number.isFinite(id) && id > 0) ids.add(id);
    });
    return ids;
  };

  const giftGroupKey = (parentId, variantId) => `${String(parentId)}:${String(variantId)}`;

  const getRequiredGiftQtyForVariant = (cart, parentId, giftVariantId, pickOne, allowedIds = null) => {
    if (pickOne) {
      return getRequiredGiftQty(cart, parentId, giftVariantId, true);
    }
    if (allowedIds && allowedIds.size > 0 && !allowedIds.has(Number(giftVariantId))) {
      return 0;
    }
    return getParentQuantityMap(cart)[String(parentId)] || 0;
  };

  const applyQuantityUpdatesToCart = (cart, updates) => {
    if (!cart || !Array.isArray(cart.items) || !updates || Object.keys(updates).length === 0) {
      return cart;
    }

    const items = cart.items.map((item) => {
      if (updates[item.key] == null) return item;
      return { ...item, quantity: updates[item.key] };
    }).filter((item) => item.quantity > 0);

    return { ...cart, items };
  };

  const parseGiftMapFromHtml = (html) => {
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const node = doc.querySelector("#farmry-free-gift-map");
      if (!node) return {};
      return JSON.parse((node.textContent || "").trim() || "{}");
    } catch (error) {
      console.error("Free gift map parse failed:", error);
      return {};
    }
  };

  const fetchGiftMap = async () => {
    const response = await fetch(`${cartUrl()}?section_id=${MAP_SECTION_ID}`, {
      credentials: "same-origin"
    });
    if (!response.ok) throw new Error("Failed to fetch free gift map");
    const html = await response.text();
    return parseGiftMapFromHtml(html);
  };

  const getGiftMapForCart = async (cart) => {
    const signature = getCartParentSignature(cart);
    if (cachedGiftMap && cachedGiftMapKey === signature) {
      return cachedGiftMap;
    }
    const giftMap = await fetchGiftMap();
    cachedGiftMap = giftMap;
    cachedGiftMapKey = signature;
    return giftMap;
  };

  const invalidateGiftMapCache = () => {
    cachedGiftMap = null;
    cachedGiftMapKey = "";
  };

  const cartItemsSignature = (cart) =>
    (cart.items || [])
      .map((item) => `${item.key}:${item.quantity}`)
      .sort()
      .join("|");

  const reloadCartPageIfNeeded = (latestCart) => {
    const signature = cartItemsSignature(latestCart);
    const guard = sessionStorage.getItem(CART_RELOAD_GUARD_KEY);
    if (guard === signature) {
      writeState(latestCart);
      return latestCart;
    }
    try {
      sessionStorage.setItem(CART_RELOAD_GUARD_KEY, signature);
    } catch (error) {
      // Ignore quota / private mode failures
    }
    writeState(latestCart);
    window.location.reload();
    return latestCart;
  };

  /**
   * Cart safeguard: if a machine product has custom.free_gift metafields
   * but the matching free-gift line is missing, queue it for add.
   */
  const planMissingGifts = (cart, giftMap = {}) => {
    const giftAdds = [];
    const queued = {};

    Object.keys(giftMap).forEach((parentId) => {
      const { pickOne, gifts } = normalizeGiftMapEntry(giftMap[parentId]);
      if (!Array.isArray(gifts) || gifts.length === 0) return;

      gifts.forEach((gift) => {
        if (!gift || !gift.id) return;

        const requiredQty = pickOne
          ? getRequiredGiftQty(cart, parentId, gift.id, true)
          : getParentQuantityMap(cart)[parentId] || 0;

        if (requiredQty <= 0) return;

        const themeQty = sumThemeFreeGiftQty(cart, parentId, gift.id);
        const appQty = sumAppFreeGiftQty(cart, gift.id);
        const deficit = requiredQty - themeQty - appQty;

        if (deficit <= 0) return;

        const queueKey = `${parentId}:${gift.id}`;
        if (queued[queueKey]) return;
        queued[queueKey] = true;

        const parentChoiceLine = (cart.items || []).find((item) => {
          if (isFreeGift(item)) return false;
          if (String(item.product_id) !== String(parentId)) return false;
          if (!pickOne) return true;
          return getParentLineChoice(item) === Number(gift.id);
        });
        const choiceVariantId = parentChoiceLine ? getParentLineChoice(parentChoiceLine) : null;

        const properties = {
          [FREE_GIFT_KEY]: "true",
          [FREE_GIFT_PARENT_KEY]: String(parentId)
        };
        if (choiceVariantId) {
          properties[FREE_GIFT_CHOICE_KEY] = String(choiceVariantId);
        }

        giftAdds.push({
          id: Number(gift.id),
          quantity: deficit,
          properties
        });
      });
    });

    return giftAdds;
  };

  const planOrphanGiftCleanup = (cart, giftMap = {}) => {
    const updates = {};

    (cart.items || []).forEach((item) => {
      if (!isFreeGift(item)) return;

      const parentId = String(getProperty(item, FREE_GIFT_PARENT_KEY) || "");
      if (!parentId) return;

      const { pickOne, gifts } = normalizeGiftMapEntry(giftMap[parentId]);
      const allowedIds = getAllowedGiftVariantIds(gifts);
      let requiredQty = 0;

      if (pickOne) {
        requiredQty = getRequiredGiftQty(cart, parentId, item.variant_id, true);
      } else if (allowedIds.size === 0) {
        requiredQty = 0;
      } else if (!allowedIds.has(Number(item.variant_id))) {
        requiredQty = 0;
      } else {
        requiredQty = getRequiredGiftQtyForVariant(
          cart,
          parentId,
          item.variant_id,
          false,
          allowedIds
        );
      }

      if (requiredQty <= 0 && item.quantity !== 0) {
        updates[item.key] = 0;
      }
    });

    return updates;
  };

  /**
   * Free-gift qty follows matching machine lines (per parent + gift variant group).
   */
  const planSync = (cart, prevState = {}, giftMap = {}) => {
    const prevParents = prevState.parents || {};
    const prevGifts = prevState.gifts || {};
    const updates = { ...planOrphanGiftCleanup(cart, giftMap) };
    const paidAdds = [];
    const groups = {};

    (cart.items || []).forEach((item) => {
      if (!isFreeGift(item)) return;
      if (updates[item.key] === 0) return;

      const parentId = String(getProperty(item, FREE_GIFT_PARENT_KEY) || "");
      if (!parentId) return;

      const key = giftGroupKey(parentId, item.variant_id);
      if (!groups[key]) {
        groups[key] = { parentId, variantId: item.variant_id, lines: [] };
      }
      groups[key].lines.push(item);
    });

    Object.values(groups).forEach((group) => {
      const { pickOne, gifts } = normalizeGiftMapEntry(giftMap[group.parentId]);
      const allowedIds = getAllowedGiftVariantIds(gifts);
      const requiredQty = getRequiredGiftQtyForVariant(
        cart,
        group.parentId,
        group.variantId,
        pickOne,
        allowedIds
      );

      const activeLines = group.lines.filter((line) => updates[line.key] !== 0);
      if (requiredQty <= 0) {
        activeLines.forEach((line) => {
          if (line.quantity !== 0) updates[line.key] = 0;
        });
        return;
      }

      const currentTotal = activeLines.reduce((sum, line) => {
        const qty = updates[line.key] != null ? updates[line.key] : line.quantity;
        return sum + qty;
      }, 0);

      if (currentTotal === requiredQty) return;

      if (currentTotal < requiredQty) {
        const deficit = requiredQty - currentTotal;
        const primary = activeLines[0];
        const primaryQty = updates[primary.key] != null ? updates[primary.key] : primary.quantity;
        updates[primary.key] = primaryQty + deficit;
        activeLines.slice(1).forEach((line) => {
          if (line.quantity !== 0) updates[line.key] = 0;
        });
        return;
      }

      let surplus = currentTotal - requiredQty;
      activeLines.forEach((line) => {
        if (surplus <= 0) return;
        const lineQty = updates[line.key] != null ? updates[line.key] : line.quantity;
        const remove = Math.min(lineQty, surplus);
        const nextQty = lineQty - remove;
        updates[line.key] = nextQty;
        surplus -= remove;

        if (remove > 0) {
          const prevGiftQty = prevGifts[line.key];
          const prevParentQty = prevParents[group.parentId];
          const giftIncreased = prevGiftQty != null && lineQty > prevGiftQty;
          const machineDecreased = prevParentQty != null && requiredQty < prevParentQty;

          if (giftIncreased && !machineDecreased) {
            queuePaidExcess(cart, updates, paidAdds, group.variantId, remove);
          }
        }
      });
    });

    return {
      updates,
      paidAdds,
      hasChanges: Object.keys(updates).length > 0 || paidAdds.length > 0
    };
  };

  const refreshCartUi = (cartContent) => {
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:updated", {
        bubbles: true,
        detail: { cart: cartContent, skipFreeGiftSync: true }
      })
    );
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:refresh", {
        bubbles: true,
        detail: {
          cart: cartContent,
          replacementDelay: 0
        }
      })
    );
  };

  const fetchCart = async () => {
    const response = await fetch(`${cartUrl()}.js`, { credentials: "same-origin" });
    if (!response.ok) throw new Error("Failed to fetch cart");
    return response.json();
  };

  const postCartItems = async (items) => {
    const response = await fetch(`${cartUrl()}/add.js`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items,
        sections: miniCartSections()
      })
    });
    if (!response.ok) return null;
    return response.json();
  };

  const syncFreeGifts = async (cart) => {
    if (!cart || !Array.isArray(cart.items)) return cart;

    if (!cartNeedsGiftSync(cart)) {
      writeState(cart);
      return cart;
    }

    if (syncing) {
      pendingResync = true;
      return cart;
    }

    syncing = true;
    let resultCart = cart;
    try {
      resultCart = (await runFreeGiftSync(cart)) || cart;
    } finally {
      syncing = false;
      if (pendingResync) {
        pendingResync = false;
        try {
          const freshCart = await fetchCart();
          resultCart = (await syncFreeGifts(freshCart)) || freshCart;
        } catch (error) {
          console.error("Free gift cart re-sync failed:", error);
        }
      }
    }
    return resultCart;
  };

  const scheduleGiftSync = () => {
    clearTimeout(syncDebounceTimer);
    syncDebounceTimer = setTimeout(async () => {
      try {
        const cart = await fetchCart();
        const updatedCart = await syncFreeGifts(cart);
        if (updatedCart && cartNeedsGiftSync(updatedCart)) {
          refreshCartUi(updatedCart);
        }
      } catch (error) {
        console.error("Free gift cart fetch failed:", error);
      }
    }, SYNC_DEBOUNCE_MS);
  };

  window.farmrySyncFreeGiftsIfNeeded = async (cart) => {
    try {
      const baseCart = cart && Array.isArray(cart.items) ? cart : await fetchCart();
      const updatedCart = await syncFreeGifts(baseCart);
      return await fetchCart();
    } catch (error) {
      console.error("Free gift inline sync failed:", error);
      return cart || null;
    }
  };

  const runFreeGiftSync = async (cart) => {
    const hasParents = (cart.items || []).some((item) => !isFreeGift(item));
    let giftMap = {};

    if (hasParents) {
      try {
        giftMap = await getGiftMapForCart(cart);
      } catch (error) {
        console.error("Free gift map fetch failed:", error);
      }
    }

    const prevState = readState();
    const plan = planSync(cart, prevState, giftMap);
    const projectedCart = applyQuantityUpdatesToCart(cart, plan.updates);
    const giftAddsNeeded = planMissingGifts(projectedCart, giftMap).length > 0;

    if (!plan.hasChanges && !giftAddsNeeded) {
      writeState(cart);
      return cart;
    }

    let latestCart = cart;

    if (Object.keys(plan.updates).length > 0) {
      const updateResponse = await fetch(`${cartUrl()}/update.js`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          updates: plan.updates,
          sections: miniCartSections()
        })
      });

      if (!updateResponse.ok) return cart;
      latestCart = await updateResponse.json();
    }

    const giftAdds = planMissingGifts(latestCart, giftMap);

    try {
      if (giftAdds.length > 0) {
        invalidateGiftMapCache();
        const giftAddJson = await postCartItems(giftAdds);
        if (!giftAddJson) return cart;
        latestCart = await fetchCart();
        latestCart.sections = giftAddJson.sections || latestCart.sections;
      }

      if (plan.paidAdds.length > 0) {
        const addJson = await postCartItems(plan.paidAdds);
        if (!addJson) return cart;

        if (isCartPage()) {
          latestCart = await fetchCart();
          return reloadCartPageIfNeeded(latestCart);
        }

        latestCart = await fetchCart();
        latestCart.sections = addJson.sections || latestCart.sections;
      } else if (isCartPage() && (giftAdds.length > 0 || Object.keys(plan.updates).length > 0)) {
        return reloadCartPageIfNeeded(latestCart);
      }

      writeState(latestCart);
      return latestCart;
    } catch (error) {
      console.error("Free gift cart sync failed:", error);
      return cart;
    }
  };

  document.documentElement.addEventListener("cart:updated", (event) => {
    if (event.detail && event.detail.skipFreeGiftSync) return;
    scheduleGiftSync();
  });

  const syncFromServer = () => {
    scheduleGiftSync();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", syncFromServer);
  } else {
    syncFromServer();
  }
})();
